import api from '@/api/panel-api';
import {Logger} from '@vanti/vue-logger';
import {PanelLights} from './panel-lights';

const log = Logger.get('iadea-panel-lights');

/**
 * @typedef {Object} LightStatus
 * @property {string} color
 * @property {string} mode - on or off
 */

/**
 * Iadea Panel integration
 */
export class PanelLightsIadea extends PanelLights {
  accessToken;

  /**
   * @param {VuexStore} store
   * @param {string} [password]
   */
  constructor(store, password = '') {
    super(store);
    this.password = password;
    this.isLocal = location.hostname === 'localhost';
  }

  /**
   * Set LEDs on room state change
   * @param {RoomState} state
   */
  async onRoomStateChange(state) {
    const color = Colors[state];
    if (!color) {
      log.error(`invalid color for room state: ${state}`);
      return;
    }
    if (this.isLocal) {
      log.debug(`skipping setting color to`, color);
      return;
    }

    log.debug(`setting color to`, color);
    try {
      await this.setLedRetry(color);
    } catch (e) {
      log.error(e);
    }
  }

  /**
   * check the LED status is correct
   */
  runLedCheck() {
    const state = this._roomState;
    const color = Colors[state];
    if (!color) {
      log.error(`invalid color for room state: ${state}`);
      return;
    }

    this.checkAndSetLed(color);
  }

  /**
   * Check LED state, and set to color if not set to color already
   * @param {string} color
   */
  async checkAndSetLed(color) {
    try {
      const status = await this.getLedRetry();
      if (status.color.toLowerCase() !== color.toLowerCase() || status.mode !== 'on') {
        await this.setLedRetry(color);
      }
    } catch (e) {
      log.error(e);
    }
  }

  /**
   * @return {Promise<LightStatus>}
   */
  async getLedRetry() {
    try {
      return await this.getLeds();
    } catch (e) {}
    // retry in case its auth
    await this.updateAuthToken();
    return await this.getLeds();
  }

  /**
   * @param {string} color
   * @return {Promise<LightStatus>}
   */
  async setLedRetry(color) {
    try {
      return await this.setLeds(color);
    } catch (e) {}
    // retry in case its auth
    await this.updateAuthToken();
    return await this.setLeds(color);
  }

  /**
   * @return {Promise<LightStatus>}
   */
  async getLeds() {
    if (!this.accessToken) {
      await this.updateAuthToken();
    }
    const status = await api.iadea.lights.getJson(`?access_token=${this.accessToken}`);
    return status[0];
  }

  /**
   * @param {string} color
   * @return {Promise<LightStatus>}
   */
  async setLeds(color) {
    if (!this.accessToken) {
      await this.updateAuthToken();
    }
    const res = await api.iadea.lights.post(`?access_token=${this.accessToken}`, {
      name: 'frame',
      brightness: this._brightness,
      color
    });
    const status = await res.json();
    return status[0];
  }

  /**
   * Get a new auth token
   * @return {Promise<void>}
   */
  async updateAuthToken() {
    const res = await api.iadea.auth.post('', {
      'grant_type': 'password',
      'username': 'admin',
      'password': this.password
    });
    this.accessToken = (await res.json()).access_token;
  }
}

const Colors = {
  AVAILABLE: '#00FF00',
  OCCUPIED: '#FF0000',
  BOOKED: '#FFF500',
  MAINTENANCE: '#AA00AA',
  red: '#FF0000',
  green: '#00FF00',
  off: '#000000'
};
